import { fmtNum, fmtRubShort } from "@/lib/format";
import { getRuleDisplayName, reconciliationResultMessages } from "@/services/messages";
import type { DiscrepancyCardViewModel } from "./view-model";

/**
 * Helpers для рендера master-pane item-rows (фича 024 ③).
 *
 * Backend `Discrepancy.summary` имеет служебный вид
 * (`"position|code:4.5: 4 нарушений (cost, volume, rate)"`),
 * не подходящий для UI. Эти функции конструируют чистый title и
 * Δ-pill для одной строки в master-навигаторе.
 */

/**
 * Чистый title для master-row: `{ruleName}: «{workName}»`.
 * Совпадает с логикой в DiscrepancyCard header.
 *
 * Для grand-total (RC-G1) не подставляем workName — это сводное правило по
 * всему набору, имя первой попавшейся работы здесь только сбивает (см. фикс
 * grand-total card).
 */
export function getCardTitle(card: DiscrepancyCardViewModel): string {
	if (card.level === "grand-total") {
		return reconciliationResultMessages.redesign.grand_total_title;
	}
	const firstRule = card.violations[0];
	const ruleName = firstRule ? getRuleDisplayName(firstRule.ruleId) : "";
	// Агрегированные RC-P9/P10 — много разных бескодовых работ, workName первой
	// строки не репрезентативен. См. DiscrepancyCard.tsx — там же.
	const isUncodedAggregate =
		card.groupKey === "position|act-uncoded" || card.groupKey === "position|wo-uncoded";
	const workName = isUncodedAggregate
		? ""
		: (card.actSources[0]?.workName ?? card.workOrderSources[0]?.workName ?? "");
	if (!ruleName) return card.summary;
	if (!workName) return ruleName;
	return `${ruleName}: «${workName}»`;
}

export interface MasterDeltaPill {
	/** Форматированное значение, например «+1,45 М ₽» (Unicode минус для отрицательных). */
	label: string;
	/** Tone для CSS — red для error, yellow для warning. */
	tone: "red" | "yellow";
}

/**
 * Δ-pill для master-row: «знаковая» цифра расхождения первого violation с
 * числовым сравнением.
 *
 * Источник чисел — `expected`/`actual` (строки) самого violation, а НЕ
 * `trace.comparison.diff`: persistence не восстанавливает trace при загрузке
 * прогона (apps/api/.../persistence.ts:250-272 — пишет только expected/actual
 * как строки). До этого фикса pill вообще не отображался ни для какого правила.
 *
 * Единицы:
 * - RC-P5 (объём) → единица из источника (м³/шт/кг и т. д.), форматируется fmtNum.
 * - RC-P7 / RC-G1 (стоимость) → рубли, форматируется fmtRubShort.
 *
 * Возвращает `null`, если у карточки нет ни одного числового сравнения
 * (например, RC-P10 / RC-P9 / RC-P3 без P5/P7).
 */
export function getCardDelta(card: DiscrepancyCardViewModel): MasterDeltaPill | null {
	for (const v of card.violations) {
		const ruleId = v.ruleId;
		if (ruleId !== "RC-P5" && ruleId !== "RC-P7" && ruleId !== "RC-G1") continue;
		const expected = parseNumeric(v.expected);
		const actual = parseNumeric(v.actual);
		if (expected === null || actual === null) continue;
		const diff = actual - expected;
		const tone: "red" | "yellow" = card.severity === "error" ? "red" : "yellow";
		const sign = diff >= 0 ? "+" : "−"; // U+2212
		const abs = Math.abs(diff);
		let formatted: string;
		if (ruleId === "RC-P5") {
			const unit = card.workOrderSources[0]?.unit ?? card.actSources[0]?.unit ?? "";
			formatted = `${sign}${fmtNum(abs)}${unit ? ` ${unit}` : ""}`;
		} else {
			formatted = `${sign}${fmtRubShort(abs)}`;
		}
		return { label: formatted, tone };
	}
	return null;
}

function parseNumeric(v: string | number | null | undefined): number | null {
	if (v === null || v === undefined) return null;
	if (typeof v === "number") return Number.isFinite(v) ? v : null;
	const n = Number(v);
	return Number.isFinite(n) ? n : null;
}
